"use client";

import { Check } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Field primitives shared by the auth screens' bespoke dark layouts (`/signup`,
 * `/login`). These sit outside `@/components/ui` on purpose: those are the
 * token-driven app components, and the auth screens are self-contained dark
 * canvases with their own palette — mixing the two produced washed-out inputs on
 * the plum/near-black backgrounds. Extracted from `signup/signup-shared` once
 * `/login` needed the same two pieces.
 */

/** Inline validation message. Renders nothing when the field is clean, so it
 * can be left in the tree. */
export function FieldError({
  message,
  className,
}: {
  message?: string;
  className?: string;
}) {
  if (!message) return null;
  return (
    <p className={cn("mt-2 text-xs text-destructive-text", className)}>{message}</p>
  );
}

/**
 * Checkbox + label row. The input stays a native checkbox (keyboard and AT
 * support for free), drawn with `appearance-none` so it can be styled for the
 * dark canvas; the tick is a real <Check> element overlaid on it and revealed by
 * `peer-checked`.
 *
 * Deliberately NOT a `checked:[background-image:url("data:…")]` arbitrary class:
 * Tailwind emits that into the stylesheet, webpack then tries to resolve the
 * `url()` as a module, and the whole app 500s with "Module not found: Can't
 * resolve './\"data:image/svg+xml…'".
 */
export function CheckRow({
  id,
  label,
  disabled,
  className,
  boxClassName,
  labelClassName,
  registration,
}: {
  id: string;
  label: React.ReactNode;
  disabled?: boolean;
  className?: string;
  boxClassName?: string;
  labelClassName?: string;
  registration: React.ComponentProps<"input">;
}) {
  return (
    <div className={cn("flex items-start gap-3", className)}>
      {/* Dimming lives on the wrapper, not on the input and the tick separately:
       * stacking `peer-disabled:` on top of `peer-checked:` isn't a thing, and
       * a bright tick inside a dimmed box reads as a bug. */}
      <span
        className={cn(
          "relative mt-0.5 grid shrink-0 place-items-center",
          disabled && "opacity-40",
        )}
      >
        <input
          id={id}
          type="checkbox"
          disabled={disabled}
          className={cn(
            "peer size-[18px] cursor-pointer appearance-none rounded-[5px] border border-input bg-accent",
            "transition-colors duration-150 hover:border-border",
            "checked:border-primary checked:bg-primary",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
            "disabled:cursor-not-allowed",
            boxClassName,
          )}
          {...registration}
        />
        <Check
          aria-hidden="true"
          strokeWidth={3}
          className="pointer-events-none absolute size-3 text-foreground opacity-0 transition-opacity duration-150 peer-checked:opacity-100"
        />
      </span>
      <label
        htmlFor={id}
        className={cn(
          "cursor-pointer text-[13px] leading-snug text-muted-foreground",
          labelClassName,
        )}
      >
        {label}
      </label>
    </div>
  );
}
